import { createDecorator } from 'components/form'
import { FlagsContextModel } from 'config/useFlagsContext'
import { Cbo, CondutaCuidadoCompartilhadoEnum, Lotacao } from 'graphql/types.generated'
import { isEmpty, isUndefinedOrNull } from 'util/checks'
import { metaPath, MetaRoot } from 'util/metaPath'
import {
  CondutaGroupEnum,
  CuidadoCompartilhadoModel,
  DiscussaoCasoFormModel,
  EnviarGarantiaAcessoCuidadoCompartilhadoFieldsModel,
  ResponsavelCuidadoCompartilhado,
  TrocaExecutanteCuidadoCompartilhadoFieldsModel,
} from 'view/cuidados-compartilhados/model-cuidadoCompartilhado'
import {
  getCondutaDefaultValues,
  getFieldsByResponsavel,
  shouldClearCondutaValues,
} from 'view/cuidados-compartilhados/util-cuidadoCompartilhado'

const getTrocaExecutanteField = (
  field: DiscussaoCasoFormModel,
  responsavelCuidado: ResponsavelCuidadoCompartilhado
): TrocaExecutanteCuidadoCompartilhadoFieldsModel =>
  getFieldsByResponsavel(responsavelCuidado, field)?.[CondutaGroupEnum.TROCA_PROFISSIONAL_REFERENCIA]

export const dicussaoCasoCalculator = (
  path: MetaRoot<DiscussaoCasoFormModel>,
  responsavelCuidado: ResponsavelCuidadoCompartilhado,
  cidadao: CuidadoCompartilhadoModel['cidadao'],
  flags: FlagsContextModel
) => {
  const pathField = responsavelCuidado === 'EXECUTANTE' ? path.executanteFields : path.solicitanteFields

  return createDecorator(
    {
      field: pathField[CondutaGroupEnum.TROCA_PROFISSIONAL_REFERENCIA].lotacao.absolutePath(),
      updates: {
        [pathField[CondutaGroupEnum.TROCA_PROFISSIONAL_REFERENCIA].cbo.absolutePath()]: (
          value: Lotacao,
          allValues: DiscussaoCasoFormModel
        ) => {
          const isLotacaoCboIdNotEqualToCboFieldId =
            value?.cbo?.id !== getTrocaExecutanteField(allValues, responsavelCuidado)?.cbo?.id

          const shouldUpdateCboField = !isUndefinedOrNull(value) && isLotacaoCboIdNotEqualToCboFieldId

          return shouldUpdateCboField ? value.cbo : getTrocaExecutanteField(allValues, responsavelCuidado)?.cbo
        },
      },
    },
    {
      field: pathField[CondutaGroupEnum.TROCA_PROFISSIONAL_REFERENCIA].cbo.absolutePath(),
      updates: {
        [pathField[CondutaGroupEnum.TROCA_PROFISSIONAL_REFERENCIA].lotacao.absolutePath()]: (
          value: Cbo,
          allValues: DiscussaoCasoFormModel,
          prevValues: DiscussaoCasoFormModel
        ) => {
          const prevTrocaExecutanteField = getTrocaExecutanteField(prevValues, responsavelCuidado)

          const doesCboChangedAndIsNotEqualToPrevLotacaoCbo =
            !isEmpty(prevTrocaExecutanteField) && value?.id !== prevTrocaExecutanteField?.lotacao?.cbo?.id

          const shouldTurnFieldToNull = isUndefinedOrNull(value) || doesCboChangedAndIsNotEqualToPrevLotacaoCbo

          return shouldTurnFieldToNull ? null : getTrocaExecutanteField(allValues, responsavelCuidado)?.lotacao
        },
      },
    },
    {
      // limpa email se o cidadao nao ter essa informacao no cadastro
      field: pathField[CondutaGroupEnum.AGENDAR_CONSULTA].consultaCompartilhada.isCidadaoPresente.absolutePath(),
      updates: {
        [pathField[
          CondutaGroupEnum.AGENDAR_CONSULTA
        ].consultaCompartilhada.cidadaoParticipante.contato.email.absolutePath()]: (
          value: boolean,
          allValues: DiscussaoCasoFormModel
        ) => {
          const cidadaoParticipante = getFieldsByResponsavel(responsavelCuidado, allValues)[
            CondutaGroupEnum.AGENDAR_CONSULTA
          ].consultaCompartilhada.cidadaoParticipante

          const cidadaoHasEmail = !isUndefinedOrNull(cidadaoParticipante.cidadao.contato.email)
          const newValue = cidadaoHasEmail ? cidadaoParticipante.contato?.email : null

          return !value ? newValue : cidadaoParticipante.contato.email
        },
      },
    },
    {
      // limpa telefone se o cidadao nao ter essa informacao no cadastro
      field: pathField[CondutaGroupEnum.AGENDAR_CONSULTA].consultaCompartilhada.isCidadaoPresente.absolutePath(),
      updates: {
        [pathField[
          CondutaGroupEnum.AGENDAR_CONSULTA
        ].consultaCompartilhada.cidadaoParticipante.contato.telefoneCelular.absolutePath()]: (
          value: boolean,
          allValues: DiscussaoCasoFormModel
        ) => {
          const cidadaoParticipante = getFieldsByResponsavel(responsavelCuidado, allValues)[
            CondutaGroupEnum.AGENDAR_CONSULTA
          ].consultaCompartilhada.cidadaoParticipante

          const cidadaoHasCelular = !isUndefinedOrNull(cidadaoParticipante.cidadao.contato.telefoneCelular)
          const newValue = cidadaoHasCelular ? cidadaoParticipante.contato?.telefoneCelular : null

          return !value ? newValue : cidadaoParticipante.contato.telefoneCelular
        },
      },
    },
    ...(flags.PILOTO_VINCULACAO_CUIDADO_ENABLED
      ? [
          {
            field: pathField.condutaGroup.absolutePath(),
            updates: {
              [pathField.absolutePath()]: (
                value: CondutaGroupEnum,
                allValues: DiscussaoCasoFormModel,
                prevValues: DiscussaoCasoFormModel
              ) => {
                const prevFieldsByResponsavel = getFieldsByResponsavel(responsavelCuidado, prevValues)
                const prevCondutaGroup = prevFieldsByResponsavel?.condutaGroup

                if (shouldClearCondutaValues(value, prevCondutaGroup)) {
                  const condutaDefaultValues = getCondutaDefaultValues(cidadao, responsavelCuidado)
                  return responsavelCuidado === 'EXECUTANTE'
                    ? { ...allValues.executanteFields, ...condutaDefaultValues }
                    : { ...allValues.solicitanteFields, ...condutaDefaultValues }
                }

                return responsavelCuidado === 'EXECUTANTE' ? allValues.executanteFields : allValues.solicitanteFields
              },
            },
          },
        ]
      : [])
  )
}

const meta = metaPath<EnviarGarantiaAcessoCuidadoCompartilhadoFieldsModel>()

export const agendamentoConsultaCompartilhadaDecorator = [
  createDecorator({
    field: meta.isVideochamada.absolutePath(),
    updates: {
      [meta.tipoAgendamento.absolutePath()]: (
        tipoAgendamento: CondutaCuidadoCompartilhadoEnum,
        allValues: EnviarGarantiaAcessoCuidadoCompartilhadoFieldsModel
      ) => {
        if (tipoAgendamento !== CondutaCuidadoCompartilhadoEnum.AGENDADO_CONSULTA_CIDADAO_PRESENCIAL) {
          if (allValues.isVideochamada) {
            return CondutaCuidadoCompartilhadoEnum.AGENDADO_TELEINTERCONSULTA_COM_CIDADAO
          } else {
            return CondutaCuidadoCompartilhadoEnum.AGENDADO_INTERCONSULTA_COM_CIDADAO
          }
        }
      },
    },
  }),
]
